import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { teamMemberService } from '../../utils/entityServices';
import EntityList from '../../components/management/EntityList';
import EntityForm from '../../components/management/EntityForm';
import ConfirmationModal from '../../components/management/ConfirmationModal';
import LoadingScreen from '../../components/LoadingScreen';

const TeamMembersPage = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [teamMemberToDelete, setTeamMemberToDelete] = useState(null);
  
  const { user } = useAuth();
  
  // Fetch team members on component mount
  useEffect(() => {
    fetchTeamMembers();
  }, []);
  
  // Fetch team members from the API
  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const data = await teamMemberService.getAll();
      setTeamMembers(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError('Failed to load team members. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle form submission for creating/updating a team member
  const handleSubmit = async (formData) => {
    try {
      setFormLoading(true);
      
      const payload = {
        name: formData.name,
        title: formData.title,
        credentials: formData.credentials,
        user_id: user.id
      };
      
      let result;
      
      if (editingTeamMember) {
        // Update existing team member
        result = await teamMemberService.update(editingTeamMember.id, payload);
        
        // Update the team members array with the updated team member
        setTeamMembers(prevTeamMembers => 
          prevTeamMembers.map(member => 
            member.id === result.id ? result : member
          )
        );
      } else {
        // Create new team member
        result = await teamMemberService.create(payload);
        
        // Add the new team member to the team members array
        setTeamMembers(prevTeamMembers => [...prevTeamMembers, result]);
      }
      
      setShowForm(false);
      setEditingTeamMember(null);
    } catch (err) {
      console.error('Error saving team member:', err);
      setError('Failed to save team member. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };
  
  // Handle edit button click
  const handleEdit = (teamMember) => {
    setEditingTeamMember(teamMember);
    setShowForm(true);
  };
  
  // Handle delete button click
  const handleDeleteClick = (teamMember) => {
    setTeamMemberToDelete(teamMember);
    setShowDeleteModal(true);
  };
  
  // Handle confirmation of deletion
  const handleDeleteConfirm = async () => {
    try {
      await teamMemberService.delete(teamMemberToDelete.id);
      
      // Remove the deleted team member from the team members array
      setTeamMembers(prevTeamMembers => 
        prevTeamMembers.filter(member => member.id !== teamMemberToDelete.id)
      );
      
      setTeamMemberToDelete(null);
    } catch (err) {
      console.error('Error deleting team member:', err);
      setError('Failed to delete team member. Please try again.');
    }
  };
  
  // Column definitions for the team member list
  const columns = [
    {
      header: 'Name',
      accessor: 'name',
    },
    {
      header: 'Title',
      accessor: 'title',
    },
    {
      header: 'Credentials',
      accessor: 'credentials',
    },
    {
      header: 'Created',
      accessor: 'created_at',
      type: 'date',
    },
  ];
  
  // Field definitions for the team member form
  const fields = [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
    },
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
    },
    {
      name: 'credentials',
      label: 'Credentials',
      type: 'textarea',
      rows: 3,
      placeholder: 'Education, certifications, experience, etc.'
    },
  ];
  
  // If the user is not authenticated, show loading screen
  if (!user) {
    return <LoadingScreen message="Loading..." />;
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">
        Team Members
      </h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>{error}</p>
        </div>
      )}
      
      {showForm ? (
        <>
          <button
            onClick={() => {
              setShowForm(false);
              setEditingTeamMember(null);
            }}
            className="mb-4 text-primary-600 hover:text-primary-700 flex items-center"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Back to List
          </button>
          
          <EntityForm
            initialValues={editingTeamMember || {}}
            onSubmit={handleSubmit}
            isLoading={formLoading}
            fields={fields}
            submitButtonText={editingTeamMember ? 'Update Team Member' : 'Create Team Member'}
            title={editingTeamMember ? 'Edit Team Member' : 'Create New Team Member'}
          />
        </>
      ) : (
        <EntityList
          items={teamMembers}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          addButtonText="Add Team Member"
          emptyMessage="No team members found. Click below to add one."
          title="Team Members"
          isLoading={loading}
          addLink={null}
          onAddClick={() => setShowForm(true)}
        />
      )}
      
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Team Member"
        message={`Are you sure you want to delete "${teamMemberToDelete?.name}"? This action cannot be undone.`}
        confirmButtonText="Delete"
        confirmButtonColor="red"
      />
    </div>
  );
};

export default TeamMembersPage; 